// 003 (FR-005, FR-025, FR-037): the school's logo, name, address and city, derived from the
// WebAddStudent / MAddStudent card pattern. A missing logo shows the initials crest; a missing
// address or city is omitted. The address (contract 1.4.0, amendment 2026-10-08) lets the parent
// see exactly which school a student joins.
import type { ReactNode } from "react";
import { Image, StyleSheet, View } from "react-native";

import type { Api } from "@/api/types";
import { SchoolCrest } from "@/shell/header/SchoolCrest";
import { makeStyles } from "@/theme/makeStyles";
import { Text } from "@/ui/Text";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    card: {
      paddingVertical: t.space.md,
      gap: t.space.lg,
      // On the bare ground; the school's blue crest tile marks it (Live Colour Tiles).
      backgroundColor: t.color.background,
      borderRadius: t.radius.card,
      alignSelf: "stretch",
    },
    row: { flexDirection: "row", alignItems: "center", gap: t.space.md },
    logo: {
      width: t.size.crest,
      height: t.size.crest,
      borderRadius: t.radius.card,
    },
    names: { flex: 1, gap: t.space.xs },
  }),
);

export function SchoolSummaryCard({
  school,
  children,
  testID = "school-summary",
}: {
  school: Pick<Api["SchoolByCode"], "id" | "name" | "city" | "logoUrl"> & {
    address?: string | null;
  };
  children?: ReactNode;
  testID?: string;
}) {
  const styles = useStyles();
  return (
    <View style={styles.card} testID={testID}>
      <View style={styles.row}>
        {school.logoUrl ? (
          <Image
            source={{ uri: school.logoUrl }}
            style={styles.logo}
            resizeMode="contain"
            accessibilityLabel={`${school.name} logo`}
            alt={`${school.name} logo`}
            testID="school-summary-logo"
          />
        ) : (
          <SchoolCrest
            school={{ id: school.id, name: school.name, logoUrl: null }}
          />
        )}
        <View style={styles.names}>
          <Text variant="heading" size="lg" weight="semiBold">
            {school.name}
          </Text>
          {school.address ? (
            <Text testID="school-summary-address">{school.address}</Text>
          ) : null}
          {school.city ? (
            <Text tone="muted" testID="school-summary-city">
              {school.city}
            </Text>
          ) : null}
        </View>
      </View>
      {children}
    </View>
  );
}
